import { useState } from 'react'
import { NAVIGATION_COPY } from '@/locales/en'
import AccountTfnSubmittedAnalytics from '@/common/analytics/events/AccountTfnSubmittedAnalytics'
import { useUpdateTfn } from '@/user/userQueries'
import { AccountSettingsFormLayout, AccountSettingsSuccessLayout } from './AccountSettingsFormLayout'
import { cleanTfn, formatTfn, isValidTfn, TFN_FORMATTED_MAX_LENGTH } from '../model/tfnFormat'

const PRIVACY_POLICY_URL =
    'https://cdn.prod.website-files.com/659642fe2d32d0e5e8e622ae/667e4952d3a76e4139137bd2_FINAL%20Sustainable%20Investment%20Exchange%20Privacy%20Policy%20June%202024.pdf'

type Props = {
    onSave: () => void
    onCancel: () => void
}

export default function TfnView({ onSave, onCancel }: Props) {
    const copy = NAVIGATION_COPY.account.tfnForm
    const successCopy = NAVIGATION_COPY.account.tfnSuccess
    const [tfn, setTfn] = useState('')
    const [isSaved, setIsSaved] = useState(false)
    const updateTfn = useUpdateTfn()

    const isValid = isValidTfn(tfn)
    const isLoading = updateTfn.isPending

    const handleSave = async () => {
        if (isValid) {
            try {
                await updateTfn.mutateAsync(cleanTfn(tfn))
                AccountTfnSubmittedAnalytics.track()
                setIsSaved(true)
            } catch {
                // Error toast is handled by the mutation hook
            }
        }
    }

    if (isSaved) {
        return (
            <AccountSettingsSuccessLayout
                title={successCopy.pageTitle}
                onClose={onSave}
                closeLabel={successCopy.backToAccount}
            />
        )
    }

    return (
        <AccountSettingsFormLayout
            title={copy.pageTitle}
            description={copy.description}
            onCancel={onCancel}
            onSave={handleSave}
            isLoading={isLoading}
            isValid={isValid}
            cancelLabel={copy.cancel}
            saveLabel={copy.save}
        >
            <p className="disclaimer-html self-stretch text-content-secondary text-sm font-normal leading-5">
                {copy.disclaimerPrefix}
                <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                    {copy.privacyPolicyLinkText}
                </a>
            </p>

            <div className="max-w-72 flex flex-col justify-start items-start gap-1.5">
                <label htmlFor="tfn" className="text-slate-900 text-sm font-medium leading-5">
                    {copy.tfnLabel}
                </label>
                <input
                    id="tfn"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={formatTfn(tfn)}
                    onChange={(e) => setTfn(e.target.value)}
                    placeholder={copy.tfnPlaceholder}
                    disabled={isLoading}
                    maxLength={TFN_FORMATTED_MAX_LENGTH}
                    className="w-full h-11 pl-4 pr-4 bg-white rounded-full border border-gray-300 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-base font-normal leading-6 placeholder:text-dust-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
            </div>
        </AccountSettingsFormLayout>
    )
}
